import { Injectable } from "@angular/core";
import { CostLineTypes, DetailsToIncludeEnum, EstimateStatusTypes, MarkupEnumType, TagModalTypeEnum, TagTypes } from "src/app/shared/enums/estimate/estimate.enum";
import { Estimate } from "src/app/shared/interfaces/estimate/estimate.interface";
import { TaxEntity } from "src/app/shared/interfaces/tax/tax.interface";
import {
  CostLineTypeProps,
  EstimateCostLineItemsEntityDTO,
  EstimateDetailsEntityDTO,
  EstimateDetailsSectionEntityDTO,
  EstimateEntityDTO,
  EstimateSectionDisplayRenderType,
  EstimateSectionsDisplayListDTO,
  EstimateValuesDTO
} from "src/app/shared/modals/estimate.modal";
import { decimal2, GetInvoicePayments, NumberUtil } from "src/app/shared/utils/util-function";
import { ClientVisibleVariable, getClientVisibleVariables, parseEstimateVariables } from "src/app/shared/utils/estimate-variables";
import { orderBy, round, sum } from "lodash";
import { CostlineServiceService } from "../costline-service/costline-service.service";
import { InvoiceEntityDTO, InvoiceEstimatedLineItemsDTO } from "src/app/shared/invoice/invoice.interface";
import { DiscountService } from "../discount.service";
import { LogRocketService } from "../logrocket-service/logrocket.service";
import * as _ from "lodash";
import { APPROVED_STATUSES, InvoiceStatusEnum, PUBLISH_STATUSES, UNPUBLISH_STATUSES } from "src/app/shared/enums/invoice/invoice.enum";
import { IPayment } from "src/app/shared/interfaces/payment/payment.interface";
import { invoiceTaxesPerLine } from "src/app/shared/utils/estimate-tax-mode";

@Injectable({
  providedIn: 'root'
})
export class EstimatesService {

  detailsToIncludeEnum = DetailsToIncludeEnum;
  constructor(
    private costLineService: CostlineServiceService,
    private discountService: DiscountService,
    private logRocketService: LogRocketService
  ) {
  }

  /**
   * The rows one scope shows the customer, precomputed so the template binds an array instead of
   * filtering per tick. An unreadable column renders none and is reported, never swallowed.
   */
  private clientVisibleVariablesOf(stored: string | null | undefined, scope: 'estimate' | 'section', id: string): ClientVisibleVariable[] {
    return getClientVisibleVariables(parseEstimateVariables(stored, (serialised, error) =>
      this.logRocketService.captureMessage('Unreadable estimate variables; rendered none for this scope.', {
        tags: { feature: 'estimate-variables', severity: 'warning' },
        extra: {
          scope,
          id,
          storedLength: serialised.length,
          storedStart: serialised.slice(0, 500),
          error: error instanceof Error ? error.message : String(error)
        }
      })
    ));
  }

  async mapEstimateSection(editEstimateData: Estimate, taxes: any[], costItemEnabled: boolean) {

    let estimateDetails: EstimateDetailsEntityDTO[] = [];

    for await (let element of editEstimateData.EstimateDetails) {
      element.TaxEntity = taxes.find(f => f.Id == element.TaxId);
      let dtoElement = { ...element } as EstimateDetailsEntityDTO;
      dtoElement.EstimateDetailsSections = this.convertSectionEntitiesToDTO(dtoElement.EstimateDetailsSections, element.TaxEntity, costItemEnabled);
      dtoElement.CustomFields = element.CustomFields;
      let fields = (dtoElement.CustomFields && JSON.parse(dtoElement.CustomFields)) || [];
      dtoElement.CustomFieldsData = this.getCustomFieldsValues(fields);
      dtoElement.ClientVisibleVariables = this.clientVisibleVariablesOf(element.Variables, 'estimate', element.Id);
      estimateDetails.push(new EstimateDetailsEntityDTO(dtoElement));
    }

    estimateDetails = this.renderEstimateDiscounts(estimateDetails);
    let estimateValues = this.getEstimateCalculatedValues(estimateDetails, taxes, DetailsToIncludeEnum.All, [], costItemEnabled);

    return { estimateDetails, estimateValues };
  }

  renderEstimateDiscounts(estimateDetails: EstimateDetailsEntityDTO[]) {
    if (estimateDetails?.length > 0) {
      estimateDetails.forEach(d => {
        if (d.EstimateDiscounts?.length > 0) {
          d.EstimateDiscounts = this.discountService.MapDiscounts(d.EstimateDiscounts);
        }
      });
    }
    return estimateDetails;
  }

  getCustomFieldsValues(fields: any) {

    if (typeof fields == 'string') {
      fields = JSON.parse(fields)
    }

    if (!Array.isArray(fields)) {
      fields = Object.entries(fields).map(([key, value]) => ({ [key]: value }));
    }

    return fields.map((f: { [key: string]: any }) => {
      return Object.entries(f).map(([key, value]) => ({ Name: key, Value: value }))
    }).flatMap((f: any) => f)

  }

  convertSectionEntitiesToDTO(sections: EstimateDetailsSectionEntityDTO[], taxEntity: TaxEntity, costItemEnabled: boolean): EstimateDetailsSectionEntityDTO[] {
    const roots = this.nestSectionDTOs(sections, taxEntity, costItemEnabled);
    this.rollupSectionTaxTotals(roots);
    return roots;
  }

  /**
   * Rebuilds the parent/child tree from the flat section list at ANY depth, in input order. A section
   * whose parent is not in the list stays top-level, so nothing the server priced can go missing.
   */
  private nestSectionDTOs(sections: EstimateDetailsSectionEntityDTO[], taxEntity: TaxEntity, costItemEnabled: boolean): EstimateDetailsSectionEntityDTO[] {
    const dtos = (sections || []).map((x) => this.convertSectionEntityToDTO(x, taxEntity, costItemEnabled));
    const dtoById = new Map<string, EstimateDetailsSectionEntityDTO>();
    dtos.forEach((dto) => {
      if ((dto.Id?.length || 0) > 0 && !dtoById.has(dto.Id)) {
        dtoById.set(dto.Id, dto);
      }
    });

    const parentOf = new Map<EstimateDetailsSectionEntityDTO, EstimateDetailsSectionEntityDTO>();
    dtos.forEach((dto) => {
      const parent = dtoById.get(dto.ParentSectionId || '');
      if (parent && parent !== dto) {
        parentOf.set(dto, parent);
      }
    });

    const roots: EstimateDetailsSectionEntityDTO[] = [];
    dtos.forEach((dto) => {
      const parent = parentOf.get(dto);
      // A parent chain that never reaches a root is a cycle. Such a section becomes top-level so its
      // money is still counted exactly once and every later walk over this tree stays finite.
      if (parent && this.reachesRootSection(parent, parentOf)) {
        parent.SubEstimateDetails.push(dto);
      } else {
        roots.push(dto);
      }
    });

    return roots;
  }

  private reachesRootSection(section: EstimateDetailsSectionEntityDTO, parentOf: Map<EstimateDetailsSectionEntityDTO, EstimateDetailsSectionEntityDTO>): boolean {
    const seen = new Set<EstimateDetailsSectionEntityDTO>([section]);
    let current = parentOf.get(section);
    while (current) {
      if (seen.has(current)) {
        return false;
      }
      seen.add(current);
      current = parentOf.get(current);
    }
    return true;
  }

  /**
   * Stamps CalculatedTax, TaxableTotal and IsTaxable on a section and every descendant: a stamped value
   * is SUBTREE-WIDE so an ancestor never re-adds it, and the walk's ROOT signs every level under it.
   */
  private rollupSectionTaxTotals(roots: EstimateDetailsSectionEntityDTO[]): void {
    const walk = (node: EstimateDetailsSectionEntityDTO, signOwner: EstimateDetailsSectionEntityDTO): { tax: number, taxable: number } => {
      const costlines = node.IsSelectionEnabled ? (node.EstimateCostLineItems || []).filter((x) => x.IsSelectedForSelection) : (node.EstimateCostLineItems || []);
      let tax = sum(costlines.map((c) => c.CalculatedTax || 0)) || 0;
      let taxable = sum(costlines.map((c) => c.taxableAmt || 0)) || 0;

      (node.SubEstimateDetails || []).forEach((child) => {
        // Every child is walked so it gets its own figures; only a SELECTED one adds to its parent.
        const subtree = walk(child, signOwner);
        if (!node.IsSelectionEnabled || child.IsSelectedForSelection) {
          tax += subtree.tax;
          taxable += subtree.taxable;
        }
      });

      const sign = signOwner?.IsDisplayOnly ? -1 : 1;
      node.CalculatedTax = tax * sign;
      node.TaxableTotal = taxable * sign;
      node.IsTaxable = this.checkIsSectionTaxable(node);

      return { tax, taxable };
    };

    roots.forEach((root) => walk(root, root));
  }

  private convertSectionEntityToDTO(section: EstimateDetailsSectionEntityDTO, tax: TaxEntity, costItemEnabled: boolean): EstimateDetailsSectionEntityDTO {
    let sectionDTO = {
      Id: section.Id,
      IsTemplate: section.IsTemplate,
      Name: section.Name,
      SectionWiseOrder: section.SectionWiseOrder,
      SectionOrder: section.SectionOrder,
      ParentSectionId: section.ParentSectionId,
      Units: section.Units,
      IsAssemblyOn: section.IsAssemblyOn,
      QtyIsVisible: section.QtyIsVisible,
      PriceIsVisible: section.PriceIsVisible,
      Variables: section.Variables,
      ClientVisibleVariables: this.clientVisibleVariablesOf(section.Variables, 'section', section.Id),
      UnitType: section.UnitType,
      ShowsSectionUnit: section.IsAssemblyOn === true && section.QtyIsVisible === true && section.Units > 0,
      SubEstimateDetails: [] as EstimateDetailsSectionEntityDTO[],
      IsViewable: section.IsViewable,
      RemovedBySectionId: section.RemovedBySectionId,
      ModifiedFromSectionId: section.ModifiedFromSectionId,
      IsDisplayOnly: section.IsDisplayOnly,
      IsSelectedForSelection: section.IsSelectedForSelection,
      SelectionType: section.SelectionType,
      IsSelectionEnabled: section.IsSelectionEnabled,
      EstimateCostLineItems: (section.EstimateCostLineItems || []).map((x) => {
        return {
          ...x,
          Props: this.getCostLineTypeProps(x.CostLineType),
        } as EstimateCostLineItemsEntityDTO;
      })
    } as EstimateDetailsSectionEntityDTO;

    //Setting derived costline props (calculated tax)
    //This is done as separate function on purpose, because if any costline has taxId then it should get calculated on costline wise else on estimate as a whole
    this.setCostlinesDerivedProps(sectionDTO.EstimateCostLineItems, tax, costItemEnabled);

    // No tax here: CalculatedTax, TaxableTotal and IsTaxable are SUBTREE-WIDE and are stamped once by
    // rollupSectionTaxTotals, after the tree this section belongs to exists.
    return sectionDTO;
  }

  checkIsSectionTaxable(section: EstimateDetailsSectionEntityDTO): boolean {
    let costlines = section.IsSelectionEnabled ? section.EstimateCostLineItems.filter((x) => x.IsSelectedForSelection) : section.EstimateCostLineItems;
    let subEstimateDetails = section.IsSelectionEnabled ? section.SubEstimateDetails?.filter((x) => x.IsSelectedForSelection) : section.SubEstimateDetails;

    return (costlines || []).some((c) => c.IsTaxable) || (subEstimateDetails || []).some((x) => this.checkIsSectionTaxable(x));
  }

  getCostLineTypeProps(type: CostLineTypes): CostLineTypeProps {
    let costlineProp = {} as CostLineTypeProps;
    costlineProp.type = type;
    switch (type) {
      case CostLineTypes.Labor:
        costlineProp.name = 'Labor';
        costlineProp.icon = 'i-walking';
        costlineProp.dropdownEditorType = TagModalTypeEnum.costline;
        costlineProp.description = 'Keep track of different types of labor costs. (e.g. Plumber Labor, Apprentice Tile Setter Labor)';
        costlineProp.categoryType = 'laborCategory';
        costlineProp.tagType = TagTypes.Labor;
        break;
      case CostLineTypes.Material:
        costlineProp.name = 'Material';
        costlineProp.icon = 'i-boxes';
        costlineProp.dropdownEditorType = TagModalTypeEnum.costline;
        costlineProp.description = 'Keep track of your material costs. (e.g. Cement Board, Tape)'
        costlineProp.categoryType = 'materialCategory'
        costlineProp.tagType = TagTypes.Materials;
        break;
      case CostLineTypes.Other:
        costlineProp.name = 'Other Cost';
        costlineProp.icon = 'i-coin_dollar';
        costlineProp.dropdownEditorType = TagModalTypeEnum.costline;
        costlineProp.description = 'Keep track of other project costs. (e.g. Permits, Parking)';
        costlineProp.tagType = TagTypes.Unknown;
        break;
    }
    return costlineProp || {};
  }



  calculateCostlineValues(costlines: EstimateCostLineItemsEntityDTO[]) {
    let costlineCost = 0;
    let costlineCostForCustomer = 0;
    let calculatedTax = 0;

    (costlines || []).forEach(x => {
      // A "$"-markup line whose company cost computes to zero contributes ZERO company cost; any other
      // line falls back to its price so the bucket is never understated.
      costlineCost += NumberUtil.ToNumber(x?.calculatedCompanyCost || (x?.DefaultMarkupType == MarkupEnumType.Amount ? 0 : x.Price));
      costlineCostForCustomer += NumberUtil.ToNumber(x.Price);
      calculatedTax += (x.CalculatedTax || 0);
    });
    return { costlineCost, costlineCostForCustomer, calculatedTax };
  }

  getEstimateTotals(
    estimateDetails: EstimateDetailsEntityDTO[],
    taxes: TaxEntity[],
    estimateInvoices: InvoiceEntityDTO[] = [],
    costlineTaxEnabled: boolean = false,
    isEstimateTemplate: boolean = false
  ): EstimateValuesDTO {
    let total = {} as EstimateValuesDTO;
    total.Published = {} as EstimateValuesDTO;
    total.Unpublished = {} as EstimateValuesDTO;
    total.Projected = {} as EstimateValuesDTO;
    total.AllRejected = false;

    if (isEstimateTemplate) {
      total = this.getEstimateCalculatedValues(estimateDetails, taxes);
    } else {
      const allRejected = estimateDetails.every(g => g.Status === EstimateStatusTypes.Rejected);

      if (!allRejected) {
        if (estimateInvoices?.length > 0) {
          estimateInvoices.map(f => {
            return {
              ...f,
              Tax: (!f.Tax && f.TaxId) ? taxes.find(g => g.Id == f.TaxId) ?? null : f.Tax
            }
          })
        }
        const approved = this.getEstimateCalculatedValues(estimateDetails, taxes, DetailsToIncludeEnum.Approved);
        const published = this.getEstimateCalculatedValues(estimateDetails, taxes, DetailsToIncludeEnum.Published);
        const unpublished = this.getEstimateCalculatedValues(estimateDetails, taxes, DetailsToIncludeEnum.UnPublished);
        const projected = this.getEstimateCalculatedValues(estimateDetails, taxes, DetailsToIncludeEnum.Projected, estimateInvoices);
        const combined = this.getEstimateCalculatedValues(estimateDetails, taxes);
        const rejected = this.getEstimateCalculatedValues(
          estimateDetails.filter(g => g.Status === EstimateStatusTypes.Rejected),
          taxes
        );

        total = {
          ...total,
          ...approved,
          Published: published,
          Unpublished: unpublished,
          Projected: projected,
          Combined: combined
        };

        let publishedInvoices = estimateInvoices.filter(f =>
          PUBLISH_STATUSES.includes(f.Status) || APPROVED_STATUSES.includes(f.Status)
        );
        let approvedInvoices = estimateInvoices.filter(f =>
          APPROVED_STATUSES.includes(f.Status)
        );
        const unpublishedInvoices = estimateInvoices.filter(f =>
          UNPUBLISH_STATUSES.includes(f.Status)
        );

        const notApprovedDetails = estimateDetails.filter(x =>
          x.Status !== EstimateStatusTypes.Approved &&
          x.Status !== EstimateStatusTypes.Rejected
        );

        const unpublishedDetails = estimateDetails.filter(x =>
          [EstimateStatusTypes.Draft, EstimateStatusTypes.PendingReview, EstimateStatusTypes.PendingSend,
          EstimateStatusTypes.ReviewRevisionRequested, EstimateStatusTypes.Unknown].includes(x.Status) &&
          x.Status !== EstimateStatusTypes.Rejected
        );

        let approvedInvoiceItemsFromUnapprovedEstimateLinesTotal = 0;
        let publishedInvoiceItemsFromUnapprovedEstimateLinesTotal = 0;

        for (const invoice of publishedInvoices) {

          // Approved total: approved estimates and change orders, what paid invoices covered for
          // documents that are not approved, and additional lines from those invoices.

          if (APPROVED_STATUSES.includes(invoice.Status)) {
            const linesForApproved = this.GetInvoiceLinesFromEstimateDetails(notApprovedDetails, invoice.EstimatedLineItems);
            if (linesForApproved.length > 0) {
              approvedInvoiceItemsFromUnapprovedEstimateLinesTotal += this.calculateInvoiceTotalValues(
                { ...invoice, EstimatedLineItems: linesForApproved },
                costlineTaxEnabled
              );

              approvedInvoices = approvedInvoices.filter(f => f.Id != invoice.Id);
            }
          }

          // Published total: published estimates and change orders, what published invoices covered
          // for documents that are not published, and additional lines from those invoices.

          const linesForPublished = this.GetInvoiceLinesFromEstimateDetails(unpublishedDetails, invoice.EstimatedLineItems);
          if (linesForPublished.length > 0) {
            publishedInvoiceItemsFromUnapprovedEstimateLinesTotal += this.calculateInvoiceTotalValues(
              { ...invoice, EstimatedLineItems: linesForPublished },
              costlineTaxEnabled
            );

            publishedInvoices = publishedInvoices.filter(f => f.Id != invoice.Id);
          }
        }

        total.ApprovedInvoiceAdditionalLinesTotal = this.calculateInvoiceAdditionalLinesTotals(approvedInvoices, taxes);
        total.UnpublishedInvoiceAdditionalLinesTotal = this.calculateInvoiceAdditionalLinesTotals(unpublishedInvoices, taxes);
        total.PublishedInvoiceAdditionalLinesTotal = this.calculateInvoiceAdditionalLinesTotals(publishedInvoices, taxes);
        total.InvoiceAdditionalLinesTotal = this.calculateInvoiceAdditionalLinesTotals(estimateInvoices, taxes);

        total.Projected.TaxableTotal += total.InvoiceAdditionalLinesTotal?.totalTaxable || 0;
        total.Projected.TotalTax += total.InvoiceAdditionalLinesTotal?.totalTax || 0;

        total.EstimatedTotal = ((total.Combined?.TotalWithTax || 0) - (total.Combined?.TotalDiscount || 0))
          - ((rejected?.TotalWithTax || 0) - (rejected?.TotalDiscount || 0));

        total.ApprovedProjectTotal = ((total.TotalWithTax || 0) - (total.TotalDiscount || 0))
          + approvedInvoiceItemsFromUnapprovedEstimateLinesTotal + (total.ApprovedInvoiceAdditionalLinesTotal?.total || 0);

        total.PublishedProjectTotal = ((total.Published.TotalWithTax || 0) - (total.Published.TotalDiscount || 0))
          + publishedInvoiceItemsFromUnapprovedEstimateLinesTotal + (total.PublishedInvoiceAdditionalLinesTotal?.total || 0);

        total.UnpublishedProjectTotal = ((total.Unpublished.TotalWithTax || 0) - (total.Unpublished.TotalDiscount || 0))
          + (total.UnpublishedInvoiceAdditionalLinesTotal?.total || 0);

        total.ProjectedProjectTotal = total.EstimatedTotal + (total.InvoiceAdditionalLinesTotal?.total || 0);
      } else {
        total.AllRejected = true;
        total.Combined = this.getEstimateCalculatedValues(estimateDetails, taxes);
        total.RejectedTotal = (total.Combined?.TotalWithTax || 0) - (total.Combined?.TotalDiscount || 0);
      }
    }

    total = { ...total, ...this.setCostlineExistance(estimateDetails) };
    return total;
  }

  calculateInvoiceAdditionalLinesTotals(publishedInvoices: InvoiceEntityDTO[], taxes: any[]): any {
    const invoicesWithData = (publishedInvoices || []).filter(
      (invoice) => (invoice?.AdditionalLineItems?.length > 0 || invoice?.InvoiceDiscounts?.filter(f => !f.IsDiscountFromEstimate)?.length > 0)
    );

    const totals = {
      total: 0,
      totalTaxable: 0,
      totalTax: 0
    };

    if (invoicesWithData?.length > 0) {
      for (var inv of invoicesWithData) {
        const taxPercent = (inv.Tax || taxes?.find(f => f.Id == inv.TaxId))?.Percent || 0;
        const preTaxDiscount = (inv.InvoiceDiscounts || []).filter(f => !f.IsDiscountFromEstimate && f.ApplyBeforeTax).reduce((sum, discount) => sum + (discount.DiscountedValue || 0), 0);
        const totalTaxable = (inv.AdditionalLineItems || []).filter(f => f.IsTaxable || f.TaxId).reduce((sum, line) => sum + (line.Price || 0), 0) - preTaxDiscount;
        const total = (inv.AdditionalLineItems || []).reduce((sum, line) => sum + (line.Price || 0), 0);
        const taxesPerLine = invoiceTaxesPerLine(inv);
        let totalTax = taxesPerLine ? 0 : totalTaxable > 0 && taxPercent > 0 ? (totalTaxable / 100) * taxPercent : 0;

        if (taxesPerLine && totalTaxable > 0) {
          totalTax += sum(
            inv.AdditionalLineItems.filter(a => a.IsTaxable || a.TaxId).map(a => {
              if (!a.Tax) {
                a.Tax = taxes.find(t => t.Id == a.TaxId);
              }

              a.LineItemTaxAmount = (!a.LineItemTaxAmount || a.LineItemTaxAmount <= 0) && a.Tax ? NumberUtil.CalculatePercentAmount(a.Price, a.Tax!.Percent, true) : a.LineItemTaxAmount;
              return a.LineItemTaxAmount || 0;
            }
            )
          );
        }

        totals.totalTax += totalTax;
        totals.totalTaxable += totalTaxable > 0 ? totalTaxable : 0;

        totals.total += NumberUtil.ToNumber(
          NumberUtil.ToNumber(total + totalTax) -
          NumberUtil.ToNumber((inv.InvoiceDiscounts || []).filter(f => !f.IsDiscountFromEstimate).reduce((sum, discount) => sum + (discount.DiscountedValue || 0), 0))
        );
      }
    }

    return totals;
  }

  private calculateInvoiceTotalValues(invoice: InvoiceEntityDTO, costlineTaxEnabled: boolean): any {
    let SubTotal = 0;
    let TaxableSubTotal = 0;
    let TotalDiscount = 0;
    let CalculatedTax = 0;
    let EstimatedLineItemsSubTotal = 0;

    if (invoice.AdditionalLineItems?.length) {
      SubTotal = _.sumBy(invoice.AdditionalLineItems, x => NumberUtil.ToNumber(x.Price));

      if (invoiceTaxesPerLine(invoice)) {
        TaxableSubTotal = _.sum(invoice.AdditionalLineItems.filter(x => x.IsTaxable || x.TaxId).map(x => NumberUtil.ToNumber(x.Price)));
        CalculatedTax += sum(
          invoice.AdditionalLineItems.filter(a => a.IsTaxable || a.Tax).map(a =>
            a.LineItemTaxAmount ? a.LineItemTaxAmount : !a.Tax ? 0 : NumberUtil.CalculatePercentAmount(a.Price, a.Tax!.Percent, true)
          )
        );

      } else {
        TaxableSubTotal = _.sumBy(invoice.AdditionalLineItems, x =>
          x.IsTaxable
            ? NumberUtil.ToNumber(x.Price)
            : (!x.IsTaxable && x.LineItemTaxableAmount
              ? NumberUtil.ToNumber(x.LineItemTaxableAmount)
              : 0)
        );
      }


    }

    if (invoice.EstimatedLineItems?.length) {
      EstimatedLineItemsSubTotal += NumberUtil.ToNumber(_.sumBy(invoice.EstimatedLineItems, x => NumberUtil.ToNumber(x.InvoicedPrice)), 2);
      SubTotal += EstimatedLineItemsSubTotal;

      TaxableSubTotal += _.sumBy(invoice.EstimatedLineItems, x =>
        NumberUtil.ToNumber(x.LineItemTaxableAmt ?? (costlineTaxEnabled ? x.InvoicedPrice : 0))
      );
    }

    if (costlineTaxEnabled && invoice.EstimatedLineItems?.length) {
      CalculatedTax += _.sumBy(
        invoice.EstimatedLineItems.filter(e => e.CostlineTax && e.InvoicedPrice),
        e => NumberUtil.ToNumber((e.CostlineTax / e.CostlineAmt) * e.InvoicedPrice)
      );
    } else {
      if (invoice.Tax) {
        CalculatedTax = NumberUtil.CalculatePercentAmount(TaxableSubTotal, invoice.Tax.Percent, true);
      }
    }

    const disObj = this.discountService.CalculateDiscount(SubTotal, TaxableSubTotal, CalculatedTax, invoice.Tax?.Percent, invoice.InvoiceDiscounts);

    if (disObj) {
      TotalDiscount = disObj?.DiscountValue || 0;
      TaxableSubTotal = disObj?.TaxableTotal || 0;
      CalculatedTax = disObj?.TotalTax || 0;
    }

    return (SubTotal + CalculatedTax) - (TotalDiscount || 0);
  }

  GetInvoiceLinesFromEstimateDetails(
    estimateDetails: EstimateDetailsEntityDTO[],
    invoiceLines: InvoiceEstimatedLineItemsDTO[]
  ): InvoiceEstimatedLineItemsDTO[] {

    if (!invoiceLines?.length || !estimateDetails?.length) return [];

    const validSectionIds = new Set<string>();
    const validCostLineIds = new Set<string>();

    estimateDetails.forEach(detail => {
      detail.EstimateDetailsSections.forEach(section => {
        validSectionIds.add(section.Id);
        section.EstimateCostLineItems.forEach(item => {
          validCostLineIds.add(item.Id);
        });
      });
    });

    return invoiceLines.filter(line =>
      validSectionIds.has(line.SectionId) || validCostLineIds.has(line.CostLineId)
    );
  }


  getEstimateCalculatedValues(
    estimateDetails: EstimateDetailsEntityDTO[],
    taxes: TaxEntity[],
    dataToInclude: DetailsToIncludeEnum = DetailsToIncludeEnum.All,
    estimateInvoices: InvoiceEntityDTO[] = [],
    costItemEnabled: boolean = false
  ): EstimateValuesDTO {
    const total: any = {
      TotalLaborCost: 0,
      TotalMaterialCost: 0,
      TotalOtherCost: 0,
      TotalLaborCostWithMarkup: 0,
      TotalMaterialCostWithMarkup: 0,
      TotalOtherCostWithMarkup: 0,
      TotalTax: 0,
      TotalTaxBeforeDiscount: 0,
      TaxableTotal: 0,
      TotalCompanyCost: 0,
      TotalWithoutTax: 0,
      TotalWithTax: 0,
      TotalWithTaxBeforeDiscount: 0,
      TotalLaborPercent: 0,
      TotalMaterialPercent: 0,
      TotalOtherCostPercent: 0,
      TotalCompanyPercent: 0,
      TotalProfit: 0,
      TotalMarkup: 0,
      TotalMargin: 0,
      TaxPercentage: 0,
      TotalDiscount: 0,
      TotalRevenue: 0,
      LaborGrossMargin: 0,
      MaterialGrossMargin: 0,
      LaborCostlinesExists: false,
      MaterialCostlinesExists: false,
      AnyCostlineExists: false,
    };

    if (estimateDetails?.length) {
      let details = dataToInclude === this.detailsToIncludeEnum.Published ?
        estimateDetails.filter(x => ![EstimateStatusTypes.Draft, EstimateStatusTypes.PendingReview, EstimateStatusTypes.PendingSend, EstimateStatusTypes.ReviewRevisionRequested, EstimateStatusTypes.Unknown, EstimateStatusTypes.Rejected].includes(x.Status)) :
        dataToInclude === this.detailsToIncludeEnum.UnPublished ?
          estimateDetails.filter(x => [EstimateStatusTypes.Draft, EstimateStatusTypes.PendingReview, EstimateStatusTypes.PendingSend, EstimateStatusTypes.ReviewRevisionRequested, EstimateStatusTypes.Unknown].includes(x.Status) && x.Status != EstimateStatusTypes.Rejected) :
          dataToInclude === this.detailsToIncludeEnum.Approved ?
            estimateDetails.filter(f => f.Status == EstimateStatusTypes.Approved) :
            dataToInclude === this.detailsToIncludeEnum.Projected ?
              estimateDetails.filter(f => f.Status !== EstimateStatusTypes.Rejected) : estimateDetails;

      const typeWiseTotal: EstimateValuesDTO[] = [];

      if (dataToInclude === this.detailsToIncludeEnum.Approved || dataToInclude === this.detailsToIncludeEnum.Published) {
        // "Once they are fully paid, they are also considered as part of the Approved Project Total" means when the invoice is fully paid, 
        // the lines included in the invoice from an unapproved change order would be considered approved. And thus, each of their total estimate value should be added to the "Approved Project Total"

        // "If the cost items on a Draft document are invoiced..." is similar to the previous item but is discussing the "Published Project Total" instead of the "Approved Project Total".
        // It means lines included in a published invoice from a draft change order would be considered published. And thus each of their total estimate value should be added to the "Published Project Total"

        const detailsIdSet = new Set(details.map(d => d.Id));

        const otherDetails = estimateDetails.filter(f =>
          f.Status !== EstimateStatusTypes.Rejected && !detailsIdSet.has(f.Id)
        );

        if (otherDetails.length > 0) {
          const invoicePublishedStatus = [InvoiceStatusEnum.PendingPayment, InvoiceStatusEnum.Partial, InvoiceStatusEnum.Overdue];

          const relevantInvoices = estimateInvoices.filter(f =>
            dataToInclude === this.detailsToIncludeEnum.Approved
              ? f.Status === InvoiceStatusEnum.Paid || f.Status === InvoiceStatusEnum.CompletelyPaid
              : invoicePublishedStatus.includes(f.Status)
          );

          const estimatedLineItems = relevantInvoices.flatMap(f => f.EstimatedLineItems);

          const invoicedLinesDetails = this.GetDetailsWhomLinesInvoiced(otherDetails, estimatedLineItems);

          if (invoicedLinesDetails.length) {
            details.push(...invoicedLinesDetails);
          }
        }
      }

      for (const detail of details) {
        const taxPercent = taxes.find((tax) => tax.Id === detail.TaxId)?.Percent ?? detail.TaxEntity?.Percent ?? 0;
        const sectionTotals = this.calculateSectionsTotal(detail.EstimateDetailsSections, detail.TaxEntity, false, costItemEnabled);

        let totalTax = decimal2(sum(sectionTotals.map((s) => s.TotalTax)));
        let taxableTotal = decimal2(sum(sectionTotals.map((s) => s.TaxableTotal)));
        detail.TotalDiscount = 0;

        const totalWithoutTaxForDetail = sum([
          sum(sectionTotals.map((s) => s.TotalLaborCostWithMarkup)),
          sum(sectionTotals.map((s) => s.TotalMaterialCostWithMarkup)),
          sum(sectionTotals.map((s) => s.TotalOtherCostWithMarkup)),
        ]);

        // We will track the crossed-out values after handling discounts so that
        // the proper tax calculation can be derived from the discounted totals

        if (detail.EstimateDiscounts?.length > 0) {
          const totalWithoutTax = sum([
            sum(sectionTotals.map((s) => s.TotalLaborCostWithMarkup)),
            sum(sectionTotals.map((s) => s.TotalMaterialCostWithMarkup)),
            sum(sectionTotals.map((s) => s.TotalOtherCostWithMarkup)),
          ]);

          const discount = this.discountService.CalculateDiscount(
            totalWithoutTax,
            taxableTotal,
            totalTax,
            taxPercent,
            detail.EstimateDiscounts
          );

          taxableTotal = discount.TaxableTotal;
          totalTax = discount.TotalTax;
          detail.TotalDiscount = discount.DiscountValue;
          total.TotalDiscount += detail.TotalDiscount;
        }

        // Calculate crossed-out values after discounts have been applied
        let preTaxDiscountPercent = 0;
        if (detail.EstimateDiscounts?.length) {
          const preTaxDiscountAmount = decimal2(sum(
            detail.EstimateDiscounts
              .filter(d => d.ApplyBeforeTax)
              .map(d => NumberUtil.ToNumber(d.DiscountedValue))
          ));
          if (preTaxDiscountAmount && totalWithoutTaxForDetail) {
            preTaxDiscountPercent = preTaxDiscountAmount / totalWithoutTaxForDetail;
          }
        }

        let crossedOutTax = 0;
        if (preTaxDiscountPercent) {
          const taxableBeforeDiscount = taxableTotal / (1 - preTaxDiscountPercent);
          crossedOutTax = decimal2(taxableBeforeDiscount * (taxPercent / 100));
        } else {
          crossedOutTax = totalTax;
        }

        total.TotalTaxBeforeDiscount += crossedOutTax;
        total.TotalWithTaxBeforeDiscount += totalWithoutTaxForDetail + crossedOutTax;

        total.TaxableTotal += decimal2(taxableTotal) || 0;
        total.TotalTax += decimal2(totalTax) || 0;

        typeWiseTotal.push(...sectionTotals);
      }

      if (typeWiseTotal.length) {
        const aggregate = (key: keyof EstimateValuesDTO) => decimal2(sum(typeWiseTotal.map((t) => t[key])));

        total.TotalLaborCost = aggregate('TotalLaborCost');
        total.TotalMaterialCost = aggregate('TotalMaterialCost');
        total.TotalOtherCost = aggregate('TotalOtherCost');
        total.TotalLaborCostWithMarkup = aggregate('TotalLaborCostWithMarkup');
        total.TotalMaterialCostWithMarkup = aggregate('TotalMaterialCostWithMarkup');
        total.TotalOtherCostWithMarkup = aggregate('TotalOtherCostWithMarkup');

        total.TotalCompanyCost = total.TotalLaborCost + total.TotalMaterialCost + total.TotalOtherCost;
        total.TotalWithoutTax =
          total.TotalLaborCostWithMarkup +
          total.TotalMaterialCostWithMarkup +
          total.TotalOtherCostWithMarkup;
        total.TotalWithTax = total.TotalWithoutTax + total.TotalTax;
        total.TotalWithTaxBeforeDiscount = total.TotalWithoutTax + total.TotalTaxBeforeDiscount;

        const totalCompanyCostWithoutInvoiceLines =
          NumberUtil.ToNumber(
            total.TotalLaborCost + total.TotalMaterialCost + total.TotalOtherCost
          );

        const totalWithoutTax = NumberUtil.ToNumber(
          total.TotalLaborCostWithMarkup + total.TotalMaterialCostWithMarkup + total.TotalOtherCostWithMarkup
        );

        // Only computed when dataToInclude is Project. These must move the projected totals only,
        // never the published, unpublished or approved ones.

        let invoiceAdditionalTotal: any;

        if (dataToInclude === this.detailsToIncludeEnum.Projected) {
          invoiceAdditionalTotal = this.invoiceAdditionalLinesAndDiscountsTotal(estimateInvoices);

          total.TotalLaborCostWithMarkup = total.TotalLaborCostWithMarkup + invoiceAdditionalTotal.LaborTotalWithMarkup;
          total.TotalLaborCost = total.TotalLaborCost + invoiceAdditionalTotal.LaborTotal;

          total.TotalMaterialCostWithMarkup = total.TotalMaterialCostWithMarkup + invoiceAdditionalTotal.MaterialTotalWithMarkup;
          total.TotalMaterialCost = total.TotalMaterialCost + invoiceAdditionalTotal.MaterialTotal;

          total.TotalOtherCostWithMarkup = total.TotalOtherCostWithMarkup + invoiceAdditionalTotal.OtherTotalWithMarkup;
          total.TotalOtherCost = total.TotalOtherCost + invoiceAdditionalTotal.OtherTotal;
        }

        total.LaborGrossMargin = total.TotalLaborCostWithMarkup <= 0 ? 0 : decimal2(
          ((total.TotalLaborCostWithMarkup - total.TotalLaborCost) / total.TotalLaborCostWithMarkup) * 100
        );

        total.MaterialGrossMargin = total.TotalMaterialCostWithMarkup <= 0 ? 0 : decimal2(
          ((total.TotalMaterialCostWithMarkup - total.TotalMaterialCost) / total.TotalMaterialCostWithMarkup) * 100
        );

        total.TotalTax = NumberUtil.ToNumber(total.TotalTax);

        total.TotalCompanyCost = NumberUtil.ToNumber(
          total.TotalLaborCost + total.TotalMaterialCost + total.TotalOtherCost
        );

        total.TotalWithoutTax = NumberUtil.ToNumber(
          total.TotalLaborCostWithMarkup + total.TotalMaterialCostWithMarkup + total.TotalOtherCostWithMarkup
        );
        total.TotalWithTax = NumberUtil.ToNumber(total.TotalWithoutTax + total.TotalTax);

        total.TotalLaborPercent = NumberUtil.ToNumber(
          NumberUtil.CalculateTotalPercent(total.TotalWithoutTax, total.TotalLaborCost)
        );
        total.TotalMaterialPercent = NumberUtil.ToNumber(
          NumberUtil.CalculateTotalPercent(total.TotalWithoutTax, total.TotalMaterialCost)
        );
        total.TotalOtherCostPercent = NumberUtil.ToNumber(
          NumberUtil.CalculateTotalPercent(total.TotalWithoutTax, total.TotalOtherCost)
        );
        total.TotalCompanyPercent = NumberUtil.ToNumber(
          NumberUtil.CalculateTotalPercent(total.TotalWithoutTax, total.TotalCompanyCost)
        );

        total.TotalRevenue = decimal2(totalWithoutTax - (total.TotalDiscount || 0) + (invoiceAdditionalTotal?.TotalWithMarkup || 0));
        total.TotalProfit = decimal2(total.TotalRevenue - (totalCompanyCostWithoutInvoiceLines + (invoiceAdditionalTotal?.Total || 0)));
        total.TotalMarkup = decimal2(
          NumberUtil.CalculateTotalPercent((totalCompanyCostWithoutInvoiceLines + (invoiceAdditionalTotal?.Total || 0)), total.TotalProfit)
        );

        // This is Project Gross Marign
        // In project gross margin include the additional lines from the invoice that are paid.

        const totalRevenue = decimal2((totalWithoutTax - (total.TotalDiscount || 0)) + ((invoiceAdditionalTotal?.PaidTotalWithMarkup || 0)));
        const totalProfit = decimal2(totalRevenue - (totalCompanyCostWithoutInvoiceLines + ((invoiceAdditionalTotal?.PaidTotal || 0))));
        total.TotalMargin = totalRevenue <= 0 ? 0 : decimal2(
          NumberUtil.CalculateTotalPercent(totalRevenue, totalProfit)
        );

        // end //

        total.TaxableTotal = decimal2(total.TaxableTotal);
        total.TotalDiscountPercent = decimal2(
          NumberUtil.CalculateTotalPercent(total.TotalWithoutTax, total.TotalDiscount)
        );
      }

      Object.assign(total, this.setCostlineExistance(estimateDetails));
    }

    return total;
  }

  private invoiceAdditionalLinesAndDiscountsTotal(invoices: any[]): any {
    let totals = {
      Total: 0,
      TotalWithMarkup: 0,
      PaidTotal: 0,
      PaidTotalWithMarkup: 0,
      LaborTotal: 0,
      LaborTotalWithMarkup: 0,
      MaterialTotal: 0,
      MaterialTotalWithMarkup: 0,
      OtherTotal: 0,
      OtherTotalWithMarkup: 0
    }

    if ((invoices || []).length > 0) {
      const calculateTotal = (invoices: any[], paidOnly: boolean = false, costOnly: boolean = false) =>
        invoices.filter(f => !paidOnly || (paidOnly && f.Status == InvoiceStatusEnum.Paid))
          .flatMap(f => f.AdditionalLineItems || []).reduce((sum, line) => sum + ((costOnly ? (line.DefaultCostPer * line.DefaultQty) : line.Price) || 0), 0) -
        invoices.filter(f => !paidOnly || (paidOnly && f.Status == InvoiceStatusEnum.Paid))
          .flatMap(f => f.InvoiceDiscounts || []).filter(f => !f.IsDiscountFromEstimate).reduce((sum, discount) => sum + (discount.DiscountedValue || 0), 0);

      const calculateCostTotal = (invoices: any[], CostLineType: CostLineTypes, costOnly: boolean = false, paidOnly: boolean = false,) =>
        invoices.filter(f => !paidOnly || (paidOnly && f.Status == InvoiceStatusEnum.Paid))
          .flatMap(f => f.AdditionalLineItems || []).filter(f => f.CostLineType == CostLineType).reduce((sum, line) => sum + ((costOnly ? (line.DefaultCostPer * line.DefaultQty) : line.Price) || 0), 0);

      totals.Total = calculateTotal(invoices, false, true);
      totals.TotalWithMarkup = calculateTotal(invoices, false);
      totals.PaidTotal = calculateTotal(invoices, true, true);
      totals.PaidTotalWithMarkup = calculateTotal(invoices, true);
      totals.LaborTotal = calculateCostTotal(invoices, CostLineTypes.Labor, true);
      totals.LaborTotalWithMarkup = calculateCostTotal(invoices, CostLineTypes.Labor, false);
      totals.MaterialTotal = calculateCostTotal(invoices, CostLineTypes.Material, true);
      totals.MaterialTotalWithMarkup = calculateCostTotal(invoices, CostLineTypes.Material, false);
      totals.OtherTotal = calculateCostTotal(invoices, CostLineTypes.Other, true);
      totals.OtherTotalWithMarkup = calculateCostTotal(invoices, CostLineTypes.Other, false);
    }

    return totals;
  }

  setCostlineExistance(estimateDetails: EstimateDetailsEntityDTO[]) {
    let allCostlines = (estimateDetails || []).flatMap(c => c.EstimateDetailsSections || []).flatMap(c => c.EstimateCostLineItems || []);
    return {
      LaborCostlinesExists: allCostlines.some(c => c.CostLineType == CostLineTypes.Labor),
      MaterialCostlinesExists: allCostlines.some(c => c.CostLineType == CostLineTypes.Material),
      AnyCostlineExists: allCostlines.length > 0
    };
  }

  GetDetailsWhomLinesInvoiced(
    estimateDetails: EstimateDetailsEntityDTO[],
    invoiceLines: InvoiceEstimatedLineItemsDTO[]
  ): EstimateDetailsEntityDTO[] {

    if (!invoiceLines?.length || !estimateDetails?.length) return [];

    const filteredInvoiceLines = invoiceLines.filter(line => (line.InvoicedPrice || 0) !== 0);
    if (!filteredInvoiceLines.length) return [];

    const sectionIdSet = new Set(filteredInvoiceLines.map(line => line.SectionId));
    const costLineIdSet = new Set(filteredInvoiceLines.map(line => line.CostLineId));

    return estimateDetails.reduce<EstimateDetailsEntityDTO[]>((acc, detail) => {
      const filteredSections = detail.EstimateDetailsSections.reduce<typeof detail.EstimateDetailsSections>((sectionAcc, section) => {
        const sectionMatches = sectionIdSet.has(section.Id);
        const filteredCostLines = section.EstimateCostLineItems.filter(item => costLineIdSet.has(item.Id));

        if (sectionMatches || filteredCostLines.length > 0) {
          sectionAcc.push({
            ...section,
            EstimateCostLineItems: sectionMatches && filteredCostLines.length === 0
              ? section.EstimateCostLineItems
              : filteredCostLines
          });
        }

        return sectionAcc;
      }, []);

      if (filteredSections.length > 0) {
        acc.push({
          ...detail,
          EstimateDetailsSections: filteredSections
        });
      }

      return acc;
    }, []);
  }

  private calculateSectionsTotal(estimateSections: EstimateDetailsSectionEntityDTO[], appliedTax: TaxEntity, isNegative: boolean, costItemEnabled: boolean, parentSection?: EstimateDetailsSectionEntityDTO): EstimateValuesDTO[] {

    if (parentSection && parentSection.IsSelectionEnabled) {
      estimateSections = (estimateSections || []).filter(f => f.IsSelectedForSelection);
    }

    let typeWiseTotal = [] as EstimateValuesDTO[];
    (estimateSections || []).forEach(s => {

      // The sign is the branch root's IsDisplayOnly alone and every level below inherits it; a section
      // whose parent is absent from this list is walked as a root and so owns its own sign.
      if (!parentSection)
        isNegative = !!s.IsDisplayOnly;

      //direct costlines
      typeWiseTotal.push(this.calculateSectionTotal(s, appliedTax, isNegative, costItemEnabled));

      //sub sections
      if (s.SubEstimateDetails?.length) {
        typeWiseTotal.push(...this.calculateSectionsTotal(s.SubEstimateDetails, appliedTax, isNegative, costItemEnabled, s));
      }
    });
    return typeWiseTotal;
  }

  private calculateSectionTotal(estimateSection: EstimateDetailsSectionEntityDTO, appliedTax: TaxEntity, isNegative: boolean, costItemEnabled: boolean): EstimateValuesDTO {
    let totalValues = {} as EstimateValuesDTO;
    if ((Object.entries(estimateSection)?.length || 0) > 0) {

      let costlines = estimateSection.IsSelectionEnabled ? estimateSection.EstimateCostLineItems.filter((x) => x.IsSelectedForSelection) : estimateSection.EstimateCostLineItems;

      //Setting derived costline props (calculated tax)
      this.setCostlinesDerivedProps(costlines, appliedTax, costItemEnabled);

      let laborCosts = this.calculateCostlineValues(costlines.filter(x => x.CostLineType == CostLineTypes.Labor));
      // MaterialInventory is a legacy imported type; it counts as material so a priced line can never
      // be visible on the shared document yet missing from its totals.
      let materialCosts = this.calculateCostlineValues(costlines.filter(x => x.CostLineType == CostLineTypes.Material || x.CostLineType == CostLineTypes.MaterialInventory));
      // Other is the catch-all: every remaining type (Note, Discount, Rebate, and any value outside
      // the enum) still prices.
      let otherCosts = this.calculateCostlineValues(costlines.filter(x => x.CostLineType != CostLineTypes.Labor && x.CostLineType != CostLineTypes.Material && x.CostLineType != CostLineTypes.MaterialInventory));
      let taxableTotal = costlines.reduce((acc: any, value: EstimateCostLineItemsEntityDTO) => {
        if (value.IsTaxable) {
          if (value.IsTaxCompanyCostOnly)
            return acc + value.calculatedCompanyCost
          else
            return acc + round(value.Price, 2)
        }
        return acc;
      }, 0);

      if (isNegative) {
        return {
          TotalLaborCost: laborCosts.costlineCost * -1,
          TotalLaborCostWithMarkup: laborCosts.costlineCostForCustomer * -1,
          TotalMaterialCost: materialCosts.costlineCost * -1,
          TotalMaterialCostWithMarkup: materialCosts.costlineCostForCustomer * -1,
          TotalOtherCost: otherCosts.costlineCost * -1,
          TotalOtherCostWithMarkup: otherCosts.costlineCostForCustomer * -1,
          TotalTax: (laborCosts.calculatedTax + materialCosts.calculatedTax + otherCosts.calculatedTax) * -1,
          TaxableTotal: taxableTotal * -1
        } as EstimateValuesDTO;
      } else {
        return {
          TotalLaborCost: laborCosts.costlineCost,
          TotalLaborCostWithMarkup: laborCosts.costlineCostForCustomer,
          TotalMaterialCost: materialCosts.costlineCost,
          TotalMaterialCostWithMarkup: materialCosts.costlineCostForCustomer,
          TotalOtherCost: otherCosts.costlineCost,
          TotalOtherCostWithMarkup: otherCosts.costlineCostForCustomer,
          TotalTax: laborCosts.calculatedTax + materialCosts.calculatedTax + otherCosts.calculatedTax,
          TaxableTotal: taxableTotal
        } as EstimateValuesDTO;
      }

    }
    return totalValues;
  }

  private setCostlinesDerivedProps(costlines: EstimateCostLineItemsEntityDTO[], appliedTax: TaxEntity, costlineTaxEnabled: boolean) {

    if (costlines?.length) {
      costlines.forEach((c) => {
        this.costLineService.setDerivedProps(c);
      });
      if (costlineTaxEnabled) {
        costlines.forEach(c => {

          if (c.IsTaxable && (c.TaxId?.length || 0) == 0) {
            c.TaxId = appliedTax?.Id || '';
            c.TaxEntity = appliedTax;
          }


          if (c.TaxId) {
            c.CalculatedTax = this.costLineService.calculateCostlineTax(c, c.TaxEntity?.Percent || 0);
            c.taxableAmt = this.costLineService.costlineTaxableAmt(c);
          }
        });
      }
      else {
        if (appliedTax) {
          costlines.forEach(c => {
            c.CalculatedTax = this.costLineService.calculateCostlineTax(c, appliedTax?.Percent || 0);
            c.taxableAmt = this.costLineService.costlineTaxableAmt(c);
          });
        }
      }
    }
  }

  flatEstimateCostlines(estimateDetailSection: EstimateDetailsSectionEntityDTO[]) {
    let estimateCostLines: EstimateCostLineItemsEntityDTO[] = estimateDetailSection.flatMap(x => x.EstimateCostLineItems);
    let estimateSubEstimateCostLines: EstimateCostLineItemsEntityDTO[] = estimateDetailSection.flatMap(y => y.SubEstimateDetails).flatMap(y => y.EstimateCostLineItems);
    return [...estimateCostLines, ...estimateSubEstimateCostLines];
  }

  calculateEstimateSectionTotal(estimateSection: EstimateDetailsSectionEntityDTO, displayOnlyOwner?: EstimateDetailsSectionEntityDTO): number {
    let costlines = estimateSection.EstimateCostLineItems;
    if (estimateSection.IsSelectionEnabled) {
      costlines = estimateSection.EstimateCostLineItems.filter(f => f.IsSelectedForSelection);
    }
    let sectionTotal = this.calculateCostlineValues(costlines)?.costlineCostForCustomer;

    // IsDisplayOnly alone decides the sign, and it rides down the whole sub-tree from its owner;
    // ModifiedFromSectionId is change-order provenance, not a money flag, and is often absent.
    let signOwner = displayOnlyOwner || estimateSection;
    if (signOwner?.IsDisplayOnly) {
      sectionTotal *= -1;
    }

    //Has Subsection?
    if ((estimateSection.SubEstimateDetails?.length || 0) > 0) {
      estimateSection.SubEstimateDetails
        .forEach((x) => {
          x.Total = this.calculateEstimateSectionTotal(x, signOwner) || 0;
          if ((estimateSection.IsSelectionEnabled && x.IsSelectedForSelection) || !estimateSection.IsSelectionEnabled)
            sectionTotal += x.Total;
        });
    }
    return sectionTotal;
  }



  calculateInvoiceEstimateValues(estimate: EstimateEntityDTO, estimateInvoices: InvoiceEntityDTO[]) {
    // here use the combined property to get actuals totals
    let estimateValues = estimate.EstimateValues;
    estimateInvoices ||= [];

    let additionalItemsTotal = NumberUtil.ToNumber(_.sum(estimateInvoices?.flatMap((x) => x.AdditionalLineItems || []).map(y => y.Price || 0)));

    estimate.EstimateValues.TotalInvoicedWithoutTax = NumberUtil.ToNumber(_.sum(estimateInvoices.map((x) => x.SubTotal)));
    estimate.EstimateValues.TotalInvoiced = NumberUtil.ToNumber(_.sum(estimateInvoices.map((x) => x.TotalInvoicedAmtWithTax)));
    estimate.EstimateValues.ToBeInvoiced = NumberUtil.Subtract(NumberUtil.ToNumber(estimateValues.TotalWithoutTax, 2), NumberUtil.ToNumber(estimate.EstimateValues.TotalInvoicedWithoutTax - (additionalItemsTotal || 0), 2));

    estimate.EstimateValues.PaymentToDate = NumberUtil.roundNumber(this.getTotalOfAllPayment(estimateInvoices.flatMap((x) => x.Payments)), true);
    estimate.EstimateValues.InvoiceRemainingBalance = NumberUtil.ToNumber(_.sum(estimateInvoices.filter((x) => x.Status !== InvoiceStatusEnum.Closed).map((x) => x.RemainingBalance)));

    // Closed estimates: no further invoicing expected, no outstanding balance to collect
    if (estimate.ClosedDate) {
      estimate.EstimateValues.ToBeInvoiced = 0;
      estimate.EstimateValues.InvoiceRemainingBalance = 0;
    }

    estimate.EstimateInvoicedProgress = Math.round(estimate.EstimateValues.ProjectedProjectTotal > 0 ? (NumberUtil.ToNumber(estimate.EstimateValues.TotalInvoiced, 2) / NumberUtil.ToNumber(estimate.EstimateValues.ProjectedProjectTotal, 2)) * 100 : 0);
    estimate.EstimateInvoicePaidProgress = Math.round(estimate.EstimateValues.ProjectedProjectTotal > 0 ? (NumberUtil.ToNumber(estimate.EstimateValues.PaymentToDate, 2) / NumberUtil.ToNumber(estimate.EstimateValues.ProjectedProjectTotal, 2)) * 100 : 0);

    estimate.EstimateInvoicedPercentage = Math.round(!estimate.EstimateValues.TotalWithoutTax ? 0 : (100 * ((estimate.EstimateValues.TotalInvoicedWithoutTax || 0) - (additionalItemsTotal || 0))) / (estimate.EstimateValues.TotalWithoutTax));
    estimate.EstimateInvoicedPercentage = Math.round(!estimate.EstimateValues.TotalWithTax ? 0 : (100 * (estimate.EstimateValues.PaymentToDate || 0)) / (estimate.EstimateValues.TotalWithTax - (estimate.EstimateValues.TotalDiscount || 0)));

    return estimate;
  }

  getTotalOfAllPayment(payments: IPayment[]) {
    return (GetInvoicePayments(payments) || []).length > 0 ? _.sum(GetInvoicePayments(payments).map(x => NumberUtil.ToNumber(x.Amount))) : 0;
  }

  renderDisplaySection(section: EstimateDetailsSectionEntityDTO, tags?: any, sortByOrderProp: boolean = false, checkSelection: boolean = false) {

    let mergedList = [] as EstimateSectionsDisplayListDTO[];

    let costlineItems = section.EstimateCostLineItems;
    let subEstimateDetails = section.SubEstimateDetails;

    if (checkSelection) {
      costlineItems = section.IsSelectionEnabled ? section.EstimateCostLineItems.filter((x) => x.IsSelectedForSelection) : section.EstimateCostLineItems;
      subEstimateDetails = section.IsSelectionEnabled ? section.SubEstimateDetails?.filter((x) => x.IsSelectedForSelection) : section.SubEstimateDetails;
    }

    if (costlineItems?.length) {
      let costlines = this.createDisplayRenderObject(costlineItems, EstimateSectionDisplayRenderType.costline, tags);
      costlines = this.orderSectionCostlines(costlines, sortByOrderProp);
      mergedList = mergedList.concat(costlines);
    }

    if (subEstimateDetails?.length) {
      const subSections = this.createDisplayRenderObject(subEstimateDetails, EstimateSectionDisplayRenderType.subsection, tags);
      mergedList = mergedList.concat(subSections);
    }

    return orderBy(mergedList, sortByOrderProp ? ['Order'] : ['SectionWiseOrder'], ['asc']);
  }

  orderSectionCostlines(costlines: any[], sortByOrderProp: boolean) {
    return _.orderBy(costlines, sortByOrderProp ? d => d.Order : d => d.SectionWiseOrder);
  }

  private createDisplayRenderObject(data: EstimateCostLineItemsEntityDTO[] | EstimateDetailsSectionEntityDTO[], renderedType: EstimateSectionDisplayRenderType, tags?: any): EstimateSectionsDisplayListDTO[] {

    //mapping category name for print preview 11/23/23
    if (renderedType == EstimateSectionDisplayRenderType.subsection && (data as any)?.length) {
      let costlinesOfSubsections = (data as any).flatMap((x: any) => x.EstimateCostLineItems);
      if (costlinesOfSubsections?.length)
        costlinesOfSubsections.forEach((costline: any) => {
          if ((costline.CategoryName || '') == '') {
            costline.CategoryName = (costline?.CategoryId && (tags || [])?.length > 0) ? tags.filter((x: any) => x.Id == costline?.CategoryId)[0]?.Name : (costline.CategoryName || '');
          }
        });
      data.forEach((subsection: any) => {
        if (subsection?.EstimateCostLineItems?.length)
          subsection.EstimateCostLineItems = orderBy(subsection.EstimateCostLineItems, ['Order'], ['asc']);
      })
    }
    return data.map((obj: any, i: number) => {
      obj.isTemplateOpen = false;
      obj.renderedType = renderedType;
      obj.index = i;
      //mapping category name for print preview 11/23/23
      if ((obj.CategoryName || '') == '') {
        if (renderedType == EstimateSectionDisplayRenderType.costline) obj.CategoryName = (obj?.CategoryId && (tags || [])?.length > 0) ? tags.filter((x: any) => x.Id == obj?.CategoryId)[0]?.Name : (obj.CategoryName || '');
      }
      return obj;
    });
  }

  IsPublished(status: number) {
    return status === EstimateStatusTypes.Approved ||
      status === EstimateStatusTypes.PendingApproval ||
      status === EstimateStatusTypes.RevisionRequested ||
      status === EstimateStatusTypes.Overdue;
  }

  calculateCrossedOutTotal(detail: EstimateDetailsEntityDTO, RequireTaxPerItemizedCostLineIsDisabled: boolean = true) {
    if (!detail) return 0;

    if (!RequireTaxPerItemizedCostLineIsDisabled) {
      return detail.TotalWithTax;
    }

    let subTotal: number;
    let taxableSubTotal: number;

    // Derive totals from the sections when they are present: the detail-level SubTotal and
    // TotalWithoutTax do not move when the customer changes their selection.
    if (detail.EstimateDetailsSections?.length > 0) {
      // Convert flat sections to hierarchical if needed so selection filtering works correctly
      let sections = detail.EstimateDetailsSections;
      const hasFlatSections = sections.some(s => (s.ParentSectionId?.length || 0) > 0);
      if (hasFlatSections) {
        sections = this.convertSectionEntitiesToDTO(sections, detail.TaxEntity, false);
      }

      const sectionTotals = this.calculateSectionsTotal(sections, detail.TaxEntity, false, false);
      subTotal = sum([
        sum(sectionTotals.map(s => s.TotalLaborCostWithMarkup || 0)),
        sum(sectionTotals.map(s => s.TotalMaterialCostWithMarkup || 0)),
        sum(sectionTotals.map(s => s.TotalOtherCostWithMarkup || 0)),
      ]);
      taxableSubTotal = sum(sectionTotals.map(s => s.TaxableTotal || 0));
    } else {
      // Fall back to pre-computed values when sections are not available
      subTotal = detail.SubTotal ?? detail.TotalWithoutTax ?? 0;
      taxableSubTotal = detail.TaxableTotal || 0;
    }

    // Use Tax.Percent if available, fall back to TaxEntity.Percent (for invoice context)
    const taxPercent = detail.Tax?.Percent ?? detail.TaxEntity?.Percent ?? 0;

    const preTaxDiscounts = (detail.EstimateDiscounts || []).filter(d => d.ApplyBeforeTax);

    if (preTaxDiscounts.length > 0) {
      const percentDiscount = sum(preTaxDiscounts.filter(d => d.IsPercentage).map(d => (d.Value || 0) / 100));
      const dollarDiscount = sum(preTaxDiscounts.filter(d => !d.IsPercentage).map(d => d.Value || 0));
      const discountPercent = percentDiscount + (subTotal ? (dollarDiscount / subTotal) : 0);

      if (discountPercent < 1) {
        // The gross-up assumes taxableSubTotal is the post-discount base; a base that is
        // already pre-discount must not be un-discounted a second time.
        let preDiscountTaxable = taxableSubTotal / (1 - discountPercent);
        if (subTotal > 0 && preDiscountTaxable > subTotal) {
          preDiscountTaxable = subTotal;
        }
        const taxValue = preDiscountTaxable * (taxPercent / 100);
        return NumberUtil.ToNumber(subTotal + taxValue);
      }
    }

    const taxValue = taxableSubTotal * (taxPercent / 100);
    return NumberUtil.ToNumber(subTotal + taxValue);
  }
}

